import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'

// 创建axios实例
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    
    // 添加认证token
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }
    
    // 显示加载状态
    const appStore = useAppStore()
    appStore.setLoading(true)
    
    return config
  },
  (error) => {
    const appStore = useAppStore()
    appStore.setLoading(false)
    return Promise.reject(error)
  }
)

// 标记是否正在刷新 token
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (reason?: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// 响应拦截器
api.interceptors.response.use(
  (response: AxiosResponse) => {
    const appStore = useAppStore()
    appStore.setLoading(false)
    return response
  },
  async (error) => {
    const appStore = useAppStore()
    const authStore = useAuthStore()
    const originalRequest = error.config
    
    appStore.setLoading(false)
    
    // 处理401错误（未授权）
    if (error.response?.status === 401) {
      // 如果是刷新 token 的请求失败，直接登出
      if (originalRequest.url?.includes('/auth/refresh')) {
        await authStore.logout()
        window.location.href = '/login'
        return Promise.reject(error)
      }
      
      // 如果已经重试过，直接登出
      if (originalRequest._retry) {
        await authStore.logout()
        window.location.href = '/login'
        return Promise.reject(error)
      }
      
      // 如果正在刷新 token，将请求加入队列
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api.request(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }
      
      // 尝试刷新 token
      if (authStore.hasRefreshToken) {
        originalRequest._retry = true
        isRefreshing = true
        
        try {
          const refreshSuccess = await authStore.refreshAccessToken()
          if (refreshSuccess) {
            const newToken = authStore.token
            processQueue(null, newToken)
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return api.request(originalRequest)
          } else {
            processQueue(new Error('Refresh failed'), null)
            await authStore.logout()
            window.location.href = '/login'
            return Promise.reject(error)
          }
        } catch (refreshError) {
          processQueue(refreshError, null)
          await authStore.logout()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }
      
      // 没有 refresh token，直接登出
      await authStore.logout()
      window.location.href = '/login'
      return Promise.reject(error)
    }
    
    // 处理429错误（请求过多）
    if (error.response?.status === 429) {
      appStore.addNotification({
        type: 'warning',
        title: '請求過於頻繁',
        message: '請稍後再試'
      })
      return Promise.reject(error)
    }
    
    // 处理其他错误
    const errorMessage = error.response?.data?.error?.message || error.message || '请求失败'
    
    // 显示错误通知
    appStore.addNotification({
      type: 'error',
      title: '请求错误',
      message: errorMessage
    })
    
    return Promise.reject(error)
  }
)

// API方法封装
export const apiClient = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) => 
    api.get<T>(url, config),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => 
    api.post<T>(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => 
    api.put<T>(url, data, config),
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => 
    api.patch<T>(url, data, config),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig) => 
    api.delete<T>(url, config)
}

// 文件上传
export const uploadFile = async (file: File, onProgress?: (progress: number) => void) => {
  const formData = new FormData()
  formData.append('file', file)
  
  return api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    }
  })
}

// 下载文件
export const downloadFile = async (url: string, filename?: string) => {
  const response = await api.get(url, {
    responseType: 'blob'
  })
  
  const blob = new Blob([response.data])
  const downloadUrl = window.URL.createObjectURL(blob)
  
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename || 'download'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  window.URL.revokeObjectURL(downloadUrl)
}

export { api }
export default api